import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CustomerService } from 'src/app/services/customer.service';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { GeneralService } from 'src/app/services/general.service';
import { TarifasService } from 'src/app/services/tarifas.service';
import { UsuarioService } from 'src/app/services/usuario.service';

type SpecialtyFilter = 'all' | 'active' | 'inactive';

interface SpecialtyColor {
  color_id: number;
  color_familia: string;
  color_hex: string;
  color_rgb: string;
}

interface ColorGroup {
  family: string;
  colors: SpecialtyColor[];
}

interface UpdateSpecialtyPayload {
  spe_id: number | null;
  price: number;
  spe_name: string;
  spe_sta_id: number;
  spe_min_age: number;
  spe_max_age: number;
  spe_is_ondemand: boolean;
  spe_color_id: number | null;
  spe_time_minutes: number;
  cus_ids: number[];
}

@Component({
  selector: 'app-admin-especialidades',
  templateUrl: './admin-especialidades.component.html',
  styleUrls: ['./admin-especialidades.component.css']
})
export class AdminEspecialidadesComponent implements OnInit {

  @Output() cantidadEspecialidadesChange = new EventEmitter<number>();

  // =========================================================
  // ESPECIALIDADES
  // =========================================================

  public especialidades: any[] = [];
  public loading = false;
  public searchText = '';
  public activeFilter: SpecialtyFilter = 'all';

  // =========================================================
  // USUARIO
  // =========================================================

  public usuario: any = null;

  // =========================================================
  // CUSTOMERS
  // =========================================================

  public customers: any[] = [];
  public loadingCustomers = false;

  public createCustomerToAdd: number | null = null;
  public editCustomerToAdd: number | null = null;

  // =========================================================
  // COLORES
  // =========================================================

  public colores: SpecialtyColor[] = [];
  public colorGroups: ColorGroup[] = [];
  public loadingColors = false;

  private colorOwnersMap = new Map<number, any[]>();

  // =========================================================
  // MODALES
  // =========================================================

  public showCreateModal = false;
  public showEditModal = false;
  public showStatusChangeModal = false;

  // =========================================================
  // GUARDANDO
  // =========================================================

  public savingCreate = false;
  public savingEdit = false;
  public savingStatusChange = false;

  // =========================================================
  // CAMBIO DE ESTADO
  // =========================================================

  public statusChangeSpecialty: any = null;
  public statusChangeTargetActive = false;

  // =========================================================
  // ESPECIALIDAD SELECCIONADA
  // =========================================================

  public selectedSpecialty: any = null;

  // =========================================================
  // CONSTANTES
  // =========================================================

  private readonly ACTIVE_STATUS_ID = 3;
  private readonly INACTIVE_STATUS_ID = 4;
  private readonly MIN_ALLOWED_AGE = 0;
  private readonly MAX_ALLOWED_AGE = 150;

  // =========================================================
  // MODELOS
  // =========================================================

  public newSpecialty: any = this.getDefaultNewSpecialty();
  public editSpecialty: any = this.getDefaultEditSpecialty();

  constructor(
    public generalService: GeneralService,
    public tarifasService: TarifasService,
    private usuarioService: UsuarioService,
    private customerService: CustomerService
  ) {}

  // =========================================================
  // INIT
  // =========================================================

  ngOnInit(): void {
    if (!sessionStorage.getItem('token')) {
      this.usuarioService.logout();
      return;
    }

    this.usuario = new EncriptacionService().getUserInfo();
    this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    await Promise.all([
      this.getCompleteSubSpecialityList(),
      this.getColorList(),
      this.getCustomers()
    ]);

    this.rebuildColorOwnersMap();
  }

  // =========================================================
  // MODELOS DEFAULT
  // =========================================================

  private getDefaultNewSpecialty(): any {
    return {
      spe_name: '',
      duration: 30,
      price: null,
      spe_min_age: 0,
      spe_max_age: 150,
      spe_is_ondemand: false,
      color_id: null,
      status: 'ACTIVA',
      cus_ids: []
    };
  }

  private getDefaultEditSpecialty(): any {
    return {
      spe_name: '',
      duration: 30,
      price: null,
      spe_min_age: 0,
      spe_max_age: 150,
      spe_is_ondemand: false,
      color_id: null,
      active: true,
      cus_ids: []
    };
  }

  // =========================================================
  // RESPUESTAS ARRAY
  // =========================================================

  private extractArrayResponse(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  // =========================================================
  // CUSTOMERS
  // =========================================================

  public async getCustomers(): Promise<void> {
    this.loadingCustomers = true;

    try {
      const data: any = await this.customerService.getListCustomer();
      const response = this.extractCustomersResponse(data);

      this.customers = response
        .map((customer: any) => ({
          ...customer,
          cus_id: Number(customer?.cus_id ?? customer?.id)
        }))
        .filter((customer: any) => Number.isFinite(customer.cus_id))
        .sort((a: any, b: any) =>
          this.getCustomerNameFromObject(a).localeCompare(
            this.getCustomerNameFromObject(b),
            'es'
          )
        );

      console.log('[AdminEspecialidades] Customers:', this.customers);

    } catch (error) {
      console.error('[AdminEspecialidades] Error getCustomers:', error);
      this.customers = [];

    } finally {
      this.loadingCustomers = false;
    }
  }

  private extractCustomersResponse(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.datos?.data)) return data.datos.data;
    if (Array.isArray(data?.datos)) return data.datos;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }

  private normalizeCustomerIds(values: any): number[] {
    if (values === null || values === undefined || values === '') {
      return [];
    }

    let rawValues: any[] = [];

    if (Array.isArray(values)) {
      rawValues = values;
    } else if (typeof values === 'string') {
      rawValues = values
        .replace(/[\{\}\[\]\s]/g, '')
        .split(',')
        .filter((value: string) => value !== '');
    } else {
      rawValues = [values];
    }

    const ids = rawValues
      .map((value: any) => {
        if (typeof value === 'object' && value !== null) {
          return Number(value?.cus_id ?? value?.id);
        }

        return Number(value);
      })
      .filter((value: number) => Number.isFinite(value) && value > 0);

    return Array.from(new Set(ids));
  }

  private extractSpecialtyCustomerIds(specialty: any): number[] {
    if (!specialty) return [];

    const ids: any[] = [];

    if (specialty?.cus_ids !== null && specialty?.cus_ids !== undefined) {
      ids.push(...this.normalizeCustomerIds(specialty.cus_ids));
    }

    if (Array.isArray(specialty?.customers)) {
      ids.push(...this.normalizeCustomerIds(specialty.customers));
    }

    if (specialty?.cus_id !== null && specialty?.cus_id !== undefined) {
      ids.push(Number(specialty.cus_id));
    }

    return this.normalizeCustomerIds(ids);
  }

  private getCustomerNameFromObject(customer: any): string {
    return String(
      customer?.cus_name ??
      customer?.cus_business_name ??
      customer?.cus_social_reason ??
      customer?.cus_razon_social ??
      customer?.customer_name ??
      customer?.name ??
      `Customer ${customer?.cus_id ?? ''}`
    ).trim();
  }

  public getCustomerName(cusId: number): string {
    const customer = this.customers.find(
      (item: any) => Number(item?.cus_id) === Number(cusId)
    );

    return customer
      ? this.getCustomerNameFromObject(customer)
      : `Customer #${cusId}`;
  }

  public getAvailableCustomers(model: any): any[] {
    const selectedIds = this.normalizeCustomerIds(model?.cus_ids);

    return this.customers.filter(
      (customer: any) => !selectedIds.includes(Number(customer?.cus_id))
    );
  }

  private addCustomerToModel(model: any, cusId: any): void {
    if (!model) return;

    const normalizedId = this.normalizeNullableNumber(cusId);

    if (normalizedId === null || normalizedId <= 0) return;

    const currentIds = this.normalizeCustomerIds(model.cus_ids);

    if (currentIds.includes(normalizedId)) return;

    model.cus_ids = [...currentIds, normalizedId];
  }

  public onCreateCustomerSelected(cusId: any): void {
    if (this.savingCreate) return;

    this.addCustomerToModel(this.newSpecialty, cusId);
    this.createCustomerToAdd = null;
  }

  public onEditCustomerSelected(cusId: any): void {
    if (this.savingEdit) return;

    this.addCustomerToModel(this.editSpecialty, cusId);
    this.editCustomerToAdd = null;
  }

  private removeCustomerFromModel(model: any, cusId: number): void {
    if (!model) return;

    model.cus_ids = this.normalizeCustomerIds(model.cus_ids)
      .filter((id: number) => Number(id) !== Number(cusId));
  }

  public removeCreateCustomer(cusId: number): void {
    if (this.savingCreate) return;
    this.removeCustomerFromModel(this.newSpecialty, cusId);
  }

  public removeEditCustomer(cusId: number): void {
    if (this.savingEdit) return;
    this.removeCustomerFromModel(this.editSpecialty, cusId);
  }

  public trackByCustomerId(index: number, cusId: number): number {
    return Number(cusId);
  }

  public trackByCustomerObjectId(index: number, customer: any): number {
    return Number(customer?.cus_id);
  }

  // =========================================================
  // LISTAR ESPECIALIDADES
  // =========================================================

  public async getCompleteSubSpecialityList(): Promise<void> {
    this.loading = true;

    try {
      const data: any = await this.generalService.getCompleteSubSpecialityList();
      this.especialidades = this.extractArrayResponse(data);

      this.emitSpecialtiesCount();
      this.rebuildColorOwnersMap();

    } catch (error) {
      console.error(
        '[AdminEspecialidades] Error getCompleteSubSpecialityList:',
        error
      );

      this.especialidades = [];
      this.emitSpecialtiesCount();
      this.rebuildColorOwnersMap();

    } finally {
      this.loading = false;
    }
  }

  private emitSpecialtiesCount(): void {
    this.cantidadEspecialidadesChange.emit(
      this.getUniqueSpecialties().length
    );
  }

  // =========================================================
  // ESPECIALIDADES ÚNICAS
  // =========================================================

  private getUniqueSpecialties(): any[] {
    const unique = new Map<string, any>();

    (this.especialidades || []).forEach((item: any) => {
      const key =
        item?.spe_id !== null && item?.spe_id !== undefined
          ? `ID_${Number(item.spe_id)}`
          : `NAME_${this.normalizeSpecialtyName(item?.spe_name)}`;

      const incomingCustomerIds = this.extractSpecialtyCustomerIds(item);
      const current = unique.get(key);

      if (!current) {
        unique.set(key, {
          ...item,
          cus_ids: incomingCustomerIds
        });
        return;
      }

      const mergedCustomerIds = this.normalizeCustomerIds([
        ...this.extractSpecialtyCustomerIds(current),
        ...incomingCustomerIds
      ]);

      const currentIsAll = this.isAllSubSpecialty(current);
      const incomingIsAll = this.isAllSubSpecialty(item);

      if (!currentIsAll && incomingIsAll) {
        unique.set(key, {
          ...item,
          cus_ids: mergedCustomerIds
        });
        return;
      }

      unique.set(key, {
        ...current,
        cus_ids: mergedCustomerIds
      });
    });

    return Array.from(unique.values());
  }

  private isAllSubSpecialty(specialty: any): boolean {
    return String(specialty?.sub_spe_name || '')
      .trim()
      .toLowerCase() === '[todas]';
  }

  // =========================================================
  // FILTROS
  // =========================================================

  public setFilter(filter: SpecialtyFilter): void {
    this.activeFilter = filter;
  }

  public get filteredSpecialties(): any[] {
    let list = this.getUniqueSpecialties();
    const search = this.normalizeSpecialtyName(this.searchText);

    if (search) {
      list = list.filter((item: any) =>
        this.normalizeSpecialtyName(item?.spe_name).includes(search)
      );
    }

    if (this.activeFilter === 'active') {
      list = list.filter((item: any) => this.isSpecialtyActive(item));
    }

    if (this.activeFilter === 'inactive') {
      list = list.filter((item: any) => !this.isSpecialtyActive(item));
    }

    return list;
  }

  public getTotalSpecialties(): number {
    return this.getUniqueSpecialties().length;
  }

  public getActiveSpecialtiesCount(): number {
    return this.getUniqueSpecialties()
      .filter((item: any) => this.isSpecialtyActive(item))
      .length;
  }

  public getInactiveSpecialtiesCount(): number {
    return this.getUniqueSpecialties()
      .filter((item: any) => !this.isSpecialtyActive(item))
      .length;
  }

  // =========================================================
  // ESTADO
  // =========================================================

  public isSpecialtyActive(specialty: any): boolean {
    if (
      specialty?.spe_sta_id !== null &&
      specialty?.spe_sta_id !== undefined
    ) {
      return Number(specialty.spe_sta_id) === this.ACTIVE_STATUS_ID;
    }

    const status = String(
      specialty?.status ||
      specialty?.spe_status ||
      ''
    ).trim().toUpperCase();

    return status === 'ACTIVA' || status === 'ACTIVO';
  }

  public getStatusLabel(specialty: any): string {
    return this.isSpecialtyActive(specialty) ? 'Activa' : 'Inactiva';
  }

  public getStatusClass(specialty: any): string {
    return this.isSpecialtyActive(specialty)
      ? 'specialty-status-active'
      : 'specialty-status-inactive';
  }

  // =========================================================
  // DATOS ESPECIALIDAD
  // =========================================================

  public getSpecialtyDuration(specialty: any): number {
    const value =
      specialty?.spe_time_minutes ??
      specialty?.spe_duration ??
      specialty?.duration ??
      specialty?.appointment_duration ??
      specialty?.sub_spe_duration ??
      30;

    const duration = Number(value);

    return Number.isFinite(duration) && duration > 0
      ? duration
      : 30;
  }

  public getSpecialtyPrice(specialty: any): number {
    const rawValue =
      specialty?.sub_spe_pri_price ??
      specialty?.price;

    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === ''
    ) {
      return 0;
    }

    const value = Number(rawValue);

    return Number.isFinite(value) ? value : 0;
  }

  public getProfessionalsCount(specialty: any): number {
    const value =
      specialty?.professionals_count ??
      specialty?.qty_professionals ??
      specialty?.cantidad_profesionales ??
      0;

    const count = Number(value);

    return Number.isFinite(count) ? count : 0;
  }

  public getProfessionalsLabel(specialty: any): string {
    const quantity = this.getProfessionalsCount(specialty);

    return quantity === 1
      ? '1 profesional'
      : `${quantity} profesionales`;
  }

  public formatPrice(value: any): string {
    const numberValue = Number(value || 0);

    return numberValue.toLocaleString('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0
    });
  }

  // =========================================================
  // EDADES
  // =========================================================

  public isAgeRangeValid(specialty: any): boolean {
    if (!specialty) return false;

    if (
      specialty.spe_min_age === '' ||
      specialty.spe_min_age === null ||
      specialty.spe_min_age === undefined ||
      specialty.spe_max_age === '' ||
      specialty.spe_max_age === null ||
      specialty.spe_max_age === undefined
    ) {
      return false;
    }

    const minAge = Number(specialty.spe_min_age);
    const maxAge = Number(specialty.spe_max_age);

    if (!Number.isFinite(minAge) || !Number.isFinite(maxAge)) {
      return false;
    }

    if (
      minAge < this.MIN_ALLOWED_AGE ||
      minAge > this.MAX_ALLOWED_AGE ||
      maxAge < this.MIN_ALLOWED_AGE ||
      maxAge > this.MAX_ALLOWED_AGE
    ) {
      return false;
    }

    return minAge <= maxAge;
  }

  public getAgeRangeError(specialty: any): string {
    if (
      specialty?.spe_min_age === '' ||
      specialty?.spe_min_age === null ||
      specialty?.spe_min_age === undefined ||
      specialty?.spe_max_age === '' ||
      specialty?.spe_max_age === null ||
      specialty?.spe_max_age === undefined
    ) {
      return 'Debes ingresar la edad mínima y máxima.';
    }

    const minAge = Number(specialty.spe_min_age);
    const maxAge = Number(specialty.spe_max_age);

    if (!Number.isFinite(minAge) || !Number.isFinite(maxAge)) {
      return 'Las edades ingresadas no son válidas.';
    }

    if (
      minAge < this.MIN_ALLOWED_AGE ||
      minAge > this.MAX_ALLOWED_AGE ||
      maxAge < this.MIN_ALLOWED_AGE ||
      maxAge > this.MAX_ALLOWED_AGE
    ) {
      return `Las edades deben estar entre ${this.MIN_ALLOWED_AGE} y ${this.MAX_ALLOWED_AGE} años.`;
    }

    if (minAge > maxAge) {
      return 'La edad mínima no puede ser mayor que la edad máxima.';
    }

    return '';
  }

  // =========================================================
  // ON DEMAND
  // =========================================================

  public toggleCreateOnDemand(): void {
    if (this.savingCreate) return;

    this.newSpecialty.spe_is_ondemand =
      !this.newSpecialty.spe_is_ondemand;
  }

  public toggleEditOnDemand(): void {
    if (this.savingEdit) return;

    this.editSpecialty.spe_is_ondemand =
      !this.editSpecialty.spe_is_ondemand;
  }

  public getOnDemandTitle(isOnDemand: boolean): string {
    return isOnDemand
      ? 'Atención inmediata habilitada'
      : 'Atención inmediata deshabilitada';
  }

  public getOnDemandDescription(isOnDemand: boolean): string {
    return isOnDemand
      ? 'La especialidad podrá estar disponible dentro del flujo de atención inmediata.'
      : 'La especialidad no se mostrará dentro del flujo de atención inmediata.';
  }

  // =========================================================
  // NORMALIZACIÓN NOMBRE
  // =========================================================

  private sanitizeSpecialtyDisplayName(value: any): string {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  private normalizeSpecialtyName(value: any): string {
    return this.sanitizeSpecialtyDisplayName(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9ñ\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // =========================================================
  // LEVENSHTEIN
  // =========================================================

  private levenshteinDistance(source: string, target: string): number {
    if (source === target) return 0;
    if (!source.length) return target.length;
    if (!target.length) return source.length;

    const matrix: number[][] = [];

    for (let i = 0; i <= target.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= source.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= target.length; i++) {
      for (let j = 1; j <= source.length; j++) {
        if (target.charAt(i - 1) === source.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[target.length][source.length];
  }

  private areSpecialtyNamesEquivalent(
    firstValue: any,
    secondValue: any
  ): boolean {
    const first = this.normalizeSpecialtyName(firstValue);
    const second = this.normalizeSpecialtyName(secondValue);

    if (!first || !second) return false;
    if (first === second) return true;
    if (first.length < 5 || second.length < 5) return false;
    if (Math.abs(first.length - second.length) > 1) return false;

    return this.levenshteinDistance(first, second) <= 1;
  }

  // =========================================================
  // DUPLICADOS
  // =========================================================

  private findDuplicateSpecialty(
    name: string,
    excludeSpeId?: number
  ): any {
    return this.getUniqueSpecialties().find((specialty: any) => {
      if (
        excludeSpeId !== null &&
        excludeSpeId !== undefined &&
        Number(specialty?.spe_id) === Number(excludeSpeId)
      ) {
        return false;
      }

      return this.areSpecialtyNamesEquivalent(
        name,
        specialty?.spe_name
      );
    }) || null;
  }

  public get newSpecialtyDuplicate(): any {
    const name = this.newSpecialty?.spe_name;

    if (!name) return null;

    return this.findDuplicateSpecialty(name);
  }

  public get editSpecialtyDuplicate(): any {
    if (!this.selectedSpecialty || !this.editSpecialty?.spe_name) {
      return null;
    }

    const originalName = this.normalizeSpecialtyName(
      this.selectedSpecialty?.spe_name
    );

    const editedName = this.normalizeSpecialtyName(
      this.editSpecialty?.spe_name
    );

    if (originalName === editedName) return null;

    return this.findDuplicateSpecialty(
      this.editSpecialty.spe_name,
      Number(this.selectedSpecialty.spe_id)
    );
  }

  public onCreateSpecialtyNameBlur(): void {
    this.newSpecialty.spe_name = this.sanitizeSpecialtyDisplayName(
      this.newSpecialty.spe_name
    );
  }

  public onEditSpecialtyNameBlur(): void {
    this.editSpecialty.spe_name = this.sanitizeSpecialtyDisplayName(
      this.editSpecialty.spe_name
    );
  }

  // =========================================================
  // COLORES
  // =========================================================

  public async getColorList(): Promise<void> {
    this.loadingColors = true;

    try {
      const data: any = await this.generalService.getColorList();
      const response = this.extractArrayResponse(data);
      const uniqueColors = new Map<number, SpecialtyColor>();

      response.forEach((color: any) => {
        const colorId = this.normalizeNullableNumber(color?.color_id);

        if (colorId === null || colorId <= 0) return;

        const hex = String(color?.color_hex || '').trim();

        if (!hex || uniqueColors.has(colorId)) return;

        uniqueColors.set(colorId, {
          color_id: colorId,
          color_familia: String(
            color?.color_familia || 'Sin familia'
          ).trim(),
          color_hex: hex,
          color_rgb: String(color?.color_rgb || '').trim()
        });
      });

      this.colores = Array.from(uniqueColors.values());
      this.buildColorGroups();

    } catch (error) {
      console.error(
        '[AdminEspecialidades] Error getColorList:',
        error
      );

      this.colores = [];
      this.colorGroups = [];

    } finally {
      this.loadingColors = false;
    }
  }

  private buildColorGroups(): void {
    const groups = new Map<string, SpecialtyColor[]>();

    this.colores.forEach((color: SpecialtyColor) => {
      const family = color.color_familia || 'Sin familia';

      if (!groups.has(family)) {
        groups.set(family, []);
      }

      groups.get(family)!.push(color);
    });

    this.colorGroups = Array.from(groups.entries()).map(
      ([family, colors]) => ({
        family,
        colors
      })
    );
  }

  // =========================================================
  // COLOR ESPECIALIDAD
  // =========================================================

  public getSpecialtyColorId(specialty: any): number | null {
    return this.normalizeNullableNumber(
      specialty?.spe_color_id ??
      specialty?.color_id
    );
  }

  public getSpecialtyColor(
    specialty: any
  ): SpecialtyColor | null {
    const colorId = this.getSpecialtyColorId(specialty);

    if (colorId === null) return null;

    const catalogColor = this.colores.find(
      (color: SpecialtyColor) =>
        color.color_id === colorId
    );

    if (catalogColor) return catalogColor;

    if (specialty?.color_hex) {
      return {
        color_id: colorId,
        color_familia: String(
          specialty?.color_familia || 'Sin familia'
        ),
        color_hex: String(specialty.color_hex),
        color_rgb: String(specialty?.color_rgb || '')
      };
    }

    return null;
  }

  public getSpecialtyColorHex(specialty: any): string {
    return this.getSpecialtyColor(specialty)?.color_hex || '#FFFFFF';
  }

  public getSpecialtyColorTooltip(specialty: any): string {
    const color = this.getSpecialtyColor(specialty);

    if (!color) {
      return 'Sin color asignado';
    }

    return `${color.color_familia} · ${color.color_hex} · ID ${color.color_id}`;
  }

  // =========================================================
  // MAPA COLOR -> ESPECIALIDADES
  // =========================================================

  private rebuildColorOwnersMap(): void {
    this.colorOwnersMap.clear();

    this.getUniqueSpecialties().forEach((specialty: any) => {
      const colorId = this.getSpecialtyColorId(specialty);

      if (colorId === null) return;

      const owners = this.colorOwnersMap.get(colorId) || [];
      owners.push(specialty);

      this.colorOwnersMap.set(colorId, owners);
    });
  }

  private getColorOwners(
    colorId: number,
    excludeSpecialtyId?: number
  ): any[] {
    const normalizedColorId = this.normalizeNullableNumber(colorId);

    if (normalizedColorId === null) return [];

    const owners = this.colorOwnersMap.get(normalizedColorId) || [];

    if (
      excludeSpecialtyId !== null &&
      excludeSpecialtyId !== undefined
    ) {
      return owners.filter(
        (specialty: any) =>
          Number(specialty?.spe_id) !== Number(excludeSpecialtyId)
      );
    }

    return owners;
  }

  private isColorUsed(
    colorId: number,
    excludeSpecialtyId?: number
  ): boolean {
    return this.getColorOwners(
      colorId,
      excludeSpecialtyId
    ).length > 0;
  }

  public getColorOwnerNames(
    color: SpecialtyColor,
    excludeSpecialtyId?: number
  ): string {
    return this.getColorOwners(
      color.color_id,
      excludeSpecialtyId
    )
      .map((specialty: any) => specialty?.spe_name)
      .filter((name: string) => !!name)
      .join(', ');
  }

  // =========================================================
  // DISPONIBILIDAD COLOR
  // =========================================================

  public isColorAvailableForCreate(
    color: SpecialtyColor
  ): boolean {
    return !this.isColorUsed(color.color_id);
  }

  public isColorAvailableForEdit(
    color: SpecialtyColor
  ): boolean {
    if (!this.selectedSpecialty) return false;

    return !this.isColorUsed(
      color.color_id,
      Number(this.selectedSpecialty.spe_id)
    );
  }

  public getCreateColorTooltip(
    color: SpecialtyColor
  ): string {
    const owners = this.getColorOwnerNames(color);

    return owners
      ? `${color.color_familia} · ${color.color_hex} · En uso por: ${owners}`
      : `${color.color_familia} · ${color.color_hex} · Disponible`;
  }

  public getEditColorTooltip(
    color: SpecialtyColor
  ): string {
    if (this.isEditColorSelected(color)) {
      return `${color.color_familia} · ${color.color_hex} · Color actual de ${this.selectedSpecialty?.spe_name}`;
    }

    const excludeSpecialtyId = this.selectedSpecialty
      ? Number(this.selectedSpecialty.spe_id)
      : undefined;

    const owners = this.getColorOwnerNames(
      color,
      excludeSpecialtyId
    );

    return owners
      ? `${color.color_familia} · ${color.color_hex} · En uso por: ${owners}`
      : `${color.color_familia} · ${color.color_hex} · Disponible`;
  }

  // =========================================================
  // CONTADORES COLORES
  // =========================================================

  public getCreateAvailableColorsCount(): number {
    return this.colores.filter(
      (color: SpecialtyColor) =>
        this.isColorAvailableForCreate(color)
    ).length;
  }

  public getEditAvailableColorsCount(): number {
    return this.colores.filter(
      (color: SpecialtyColor) =>
        this.isColorAvailableForEdit(color)
    ).length;
  }

  public getCreateAvailableColorsInGroup(
    group: ColorGroup
  ): number {
    return group.colors.filter(
      (color: SpecialtyColor) =>
        this.isColorAvailableForCreate(color)
    ).length;
  }

  public getEditAvailableColorsInGroup(
    group: ColorGroup
  ): number {
    return group.colors.filter(
      (color: SpecialtyColor) =>
        this.isColorAvailableForEdit(color)
    ).length;
  }

  // =========================================================
  // SELECCIONAR COLOR
  // =========================================================

  public selectCreateColor(color: SpecialtyColor): void {
    if (
      this.savingCreate ||
      !this.isColorAvailableForCreate(color)
    ) {
      return;
    }

    this.newSpecialty.color_id = color.color_id;
  }

  public selectEditColor(color: SpecialtyColor): void {
    if (
      this.savingEdit ||
      !this.isColorAvailableForEdit(color)
    ) {
      return;
    }

    this.editSpecialty.color_id = color.color_id;
  }

  // =========================================================
  // COLOR SELECCIONADO
  // =========================================================

  public isCreateColorSelected(
    color: SpecialtyColor
  ): boolean {
    const selectedId = this.normalizeNullableNumber(
      this.newSpecialty?.color_id
    );

    return selectedId !== null &&
      selectedId === color.color_id;
  }

  public isEditColorSelected(
    color: SpecialtyColor
  ): boolean {
    const selectedId = this.normalizeNullableNumber(
      this.editSpecialty?.color_id
    );

    return selectedId !== null &&
      selectedId === color.color_id;
  }

  public getSelectedCreateColor(): SpecialtyColor | null {
    const colorId = this.normalizeNullableNumber(
      this.newSpecialty?.color_id
    );

    if (colorId === null) return null;

    return this.colores.find(
      (color: SpecialtyColor) =>
        color.color_id === colorId
    ) || null;
  }

  public getSelectedEditColor(): SpecialtyColor | null {
    const colorId = this.normalizeNullableNumber(
      this.editSpecialty?.color_id
    );

    if (colorId === null) return null;

    return this.colores.find(
      (color: SpecialtyColor) =>
        color.color_id === colorId
    ) || null;
  }

  // =========================================================
  // TRACK BY
  // =========================================================

  public trackByColorId(
    index: number,
    color: SpecialtyColor
  ): number {
    return color.color_id;
  }

  public trackByColorFamily(
    index: number,
    group: ColorGroup
  ): string {
    return group.family;
  }

  // =========================================================
  // CREAR
  // =========================================================

  public openCreateModal(): void {
    if (this.savingCreate) return;

    this.newSpecialty = this.getDefaultNewSpecialty();
    this.createCustomerToAdd = null;
    this.showCreateModal = true;
  }

  public closeCreateModal(): void {
    if (this.savingCreate) return;

    this.showCreateModal = false;
    this.createCustomerToAdd = null;
    this.newSpecialty = this.getDefaultNewSpecialty();
  }

  public canSaveNewSpecialty(): boolean {
    const name = this.sanitizeSpecialtyDisplayName(
      this.newSpecialty?.spe_name
    );

    const duration = Number(
      this.newSpecialty?.duration || 0
    );

    const price = Number(
      this.newSpecialty?.price || 0
    );

    const colorId = this.normalizeNullableNumber(
      this.newSpecialty?.color_id
    );

    const validColor =
      colorId !== null &&
      !this.isColorUsed(colorId);

    return !!(
      name &&
      !this.newSpecialtyDuplicate &&
      duration > 0 &&
      price >= 1000 &&
      price <= 150000 &&
      this.isAgeRangeValid(this.newSpecialty) &&
      validColor &&
      !this.savingCreate
    );
  }

  private buildCreateSpecialtyPayload(): UpdateSpecialtyPayload {
    return {
      spe_id: null,
      price: Number(this.newSpecialty?.price),
      spe_name: this.sanitizeSpecialtyDisplayName(
        this.newSpecialty?.spe_name
      ),
      spe_sta_id: this.ACTIVE_STATUS_ID,
      spe_min_age: Number(this.newSpecialty?.spe_min_age),
      spe_max_age: Number(this.newSpecialty?.spe_max_age),
      spe_is_ondemand: Boolean(
        this.newSpecialty?.spe_is_ondemand
      ),
      spe_color_id: this.normalizeNullableNumber(
        this.newSpecialty?.color_id
      ),
      spe_time_minutes: Number(
        this.newSpecialty?.duration
      ),
      cus_ids: this.normalizeCustomerIds(
        this.newSpecialty?.cus_ids
      )
    };
  }

  public async saveNewSpecialty(): Promise<void> {
    this.onCreateSpecialtyNameBlur();

    if (this.savingCreate) return;

    const duplicate = this.newSpecialtyDuplicate;

    if (duplicate) {
      this.generalService.modalError(
        'Especialidad ya existente',
        `Ya existe una especialidad igual o muy similar: ${duplicate.spe_name}`
      );
      return;
    }

    if (!this.isAgeRangeValid(this.newSpecialty)) {
      this.generalService.modalError(
        'Rango de edad inválido',
        this.getAgeRangeError(this.newSpecialty)
      );
      return;
    }

    const colorId = this.normalizeNullableNumber(
      this.newSpecialty?.color_id
    );

    if (colorId === null) {
      this.generalService.modalError(
        'Color obligatorio',
        'Debes seleccionar un color para la especialidad.'
      );
      return;
    }

    const owners = this.getColorOwners(colorId);

    if (owners.length > 0) {
      const names = owners
        .map((specialty: any) => specialty?.spe_name)
        .filter((name: string) => !!name)
        .join(', ');

      this.generalService.modalError(
        'Color no disponible',
        `El color seleccionado ya está asignado a: ${names}`
      );

      this.newSpecialty.color_id = null;
      return;
    }

    if (!this.canSaveNewSpecialty()) return;

    const payload: UpdateSpecialtyPayload =
      this.buildCreateSpecialtyPayload();

    console.log(
      '[AdminEspecialidades] Payload crear especialidad:',
      payload
    );

    try {
      this.savingCreate = true;
      this.generalService.modalCargando();

      const response: any =
        await this.tarifasService.postUpdateSpeciality(payload);

      if (response === undefined || response === null) {
        throw new Error(
          'postUpdateSpeciality no retornó una respuesta válida.'
        );
      }

      this.closeCreateModalAfterSave();
      await this.getCompleteSubSpecialityList();

      this.generalService.modalExitoso(
        'Especialidad creada exitosamente',
        `"${payload.spe_name}" fue creada correctamente.`
      );

    } catch (error) {
      console.error(
        '[AdminEspecialidades] Error creando especialidad:',
        error
      );

      this.generalService.modalError(
        'No fue posible crear la especialidad',
        'Ocurrió un problema al crear la especialidad. Intenta nuevamente.'
      );

    } finally {
      this.savingCreate = false;
    }
  }

  private closeCreateModalAfterSave(): void {
    this.showCreateModal = false;
    this.createCustomerToAdd = null;
    this.newSpecialty = this.getDefaultNewSpecialty();
  }

  // =========================================================
  // EDITAR
  // =========================================================

  public openEditModal(specialty: any): void {
    this.selectedSpecialty = specialty;
    this.editCustomerToAdd = null;

    this.editSpecialty = {
      spe_name: this.sanitizeSpecialtyDisplayName(
        specialty?.spe_name
      ),
      duration: this.getSpecialtyDuration(specialty),
      price: this.getSpecialtyPrice(specialty),
      spe_min_age: specialty?.spe_min_age ?? 0,
      spe_max_age: specialty?.spe_max_age ?? 150,
      spe_is_ondemand: Boolean(
        specialty?.spe_is_ondemand
      ),
      color_id: this.getSpecialtyColorId(specialty),
      active:
        Number(specialty?.spe_sta_id) ===
        this.ACTIVE_STATUS_ID,
      cus_ids: this.extractSpecialtyCustomerIds(specialty)
    };

    console.log(
      '[AdminEspecialidades] Customers edición:',
      this.editSpecialty.cus_ids
    );

    this.showEditModal = true;
  }

  public closeEditModal(): void {
    if (this.savingEdit) return;

    this.showEditModal = false;
    this.selectedSpecialty = null;
    this.editCustomerToAdd = null;
    this.editSpecialty = this.getDefaultEditSpecialty();
  }

  public toggleEditSpecialtyStatus(): void {
    if (this.savingEdit) return;

    this.editSpecialty.active =
      !this.editSpecialty.active;
  }

  public getEditStatusTitle(): string {
    return this.editSpecialty.active
      ? 'Especialidad activa'
      : 'Especialidad inactiva';
  }

  public getEditStatusDescription(): string {
    return this.editSpecialty.active
      ? 'La especialidad se encuentra disponible para ser utilizada en los flujos correspondientes.'
      : 'Las especialidades inactivas dejan de mostrarse en los flujos donde solo se consideran especialidades activas.';
  }

  public canSaveEditSpecialty(): boolean {
    if (!this.selectedSpecialty) return false;

    const name = this.sanitizeSpecialtyDisplayName(
      this.editSpecialty?.spe_name
    );

    const duration = Number(
      this.editSpecialty?.duration || 0
    );

    const price = Number(
      this.editSpecialty?.price || 0
    );

    const colorId = this.normalizeNullableNumber(
      this.editSpecialty?.color_id
    );

    const validColor =
      colorId !== null &&
      !this.isColorUsed(
        colorId,
        Number(this.selectedSpecialty.spe_id)
      );

    return !!(
      name &&
      !this.editSpecialtyDuplicate &&
      duration > 0 &&
      price >= 1000 &&
      price <= 150000 &&
      this.isAgeRangeValid(this.editSpecialty) &&
      validColor &&
      !this.savingEdit
    );
  }

  private buildEditSpecialtyPayload(): UpdateSpecialtyPayload {
    return {
      spe_id: Number(this.selectedSpecialty?.spe_id),
      price: Number(this.editSpecialty?.price),
      spe_name: this.sanitizeSpecialtyDisplayName(
        this.editSpecialty?.spe_name
      ),
      spe_sta_id: this.editSpecialty?.active
        ? this.ACTIVE_STATUS_ID
        : this.INACTIVE_STATUS_ID,
      spe_min_age: Number(this.editSpecialty?.spe_min_age),
      spe_max_age: Number(this.editSpecialty?.spe_max_age),
      spe_is_ondemand: Boolean(
        this.editSpecialty?.spe_is_ondemand
      ),
      spe_color_id: this.normalizeNullableNumber(
        this.editSpecialty?.color_id
      ),
      spe_time_minutes: Number(
        this.editSpecialty?.duration
      ),
      cus_ids: this.normalizeCustomerIds(
        this.editSpecialty?.cus_ids
      )
    };
  }

  public async saveEditSpecialty(): Promise<void> {
    this.onEditSpecialtyNameBlur();

    if (!this.selectedSpecialty || this.savingEdit) return;

    const duplicate = this.editSpecialtyDuplicate;

    if (duplicate) {
      this.generalService.modalError(
        'Nombre no disponible',
        `Ya existe otra especialidad igual o muy similar: ${duplicate.spe_name}`
      );
      return;
    }

    if (!this.isAgeRangeValid(this.editSpecialty)) {
      this.generalService.modalError(
        'Rango de edad inválido',
        this.getAgeRangeError(this.editSpecialty)
      );
      return;
    }

    const colorId = this.normalizeNullableNumber(
      this.editSpecialty?.color_id
    );

    if (colorId === null) {
      this.generalService.modalError(
        'Color obligatorio',
        'Debes seleccionar un color para la especialidad.'
      );
      return;
    }

    const otherOwners = this.getColorOwners(
      colorId,
      Number(this.selectedSpecialty?.spe_id)
    );

    if (otherOwners.length > 0) {
      const names = otherOwners
        .map((specialty: any) => specialty?.spe_name)
        .filter((name: string) => !!name)
        .join(', ');

      this.generalService.modalError(
        'Color no disponible',
        `El color seleccionado ya está asignado a: ${names}`
      );

      return;
    }

    if (!this.canSaveEditSpecialty()) return;

    const payload: UpdateSpecialtyPayload =
      this.buildEditSpecialtyPayload();

    console.log(
      '[AdminEspecialidades] Payload actualizar especialidad:',
      payload
    );

    try {
      this.savingEdit = true;
      this.generalService.modalCargando();

      const response: any =
        await this.tarifasService.postUpdateSpeciality(payload);

      if (response === undefined || response === null) {
        throw new Error(
          'postUpdateSpeciality no retornó respuesta.'
        );
      }

      this.closeEditModalAfterSave();
      await this.getCompleteSubSpecialityList();

      this.generalService.modalExitoso(
        'Especialidad actualizada exitosamente',
        'Los cambios fueron guardados correctamente.'
      );

    } catch (error) {
      console.error(
        '[AdminEspecialidades] Error actualizando especialidad:',
        error
      );

      this.generalService.modalError(
        'No fue posible actualizar la especialidad',
        'Ocurrió un problema al guardar los cambios. Intenta nuevamente.'
      );

    } finally {
      this.savingEdit = false;
    }
  }

  private closeEditModalAfterSave(): void {
    this.showEditModal = false;
    this.selectedSpecialty = null;
    this.editCustomerToAdd = null;
    this.editSpecialty = this.getDefaultEditSpecialty();
  }

  // =========================================================
  // ACTIVAR / DESACTIVAR
  // =========================================================

  public toggleSpecialtyStatus(specialty: any): void {
    if (!specialty || this.savingStatusChange) return;

    this.statusChangeSpecialty = specialty;
    this.statusChangeTargetActive =
      !this.isSpecialtyActive(specialty);

    this.showStatusChangeModal = true;
  }

  public getStatusChangeTitle(): string {
    return this.statusChangeTargetActive
      ? 'Activar especialidad'
      : 'Desactivar especialidad';
  }

  public getStatusChangeDescription(): string {
    const name = this.sanitizeSpecialtyDisplayName(
      this.statusChangeSpecialty?.spe_name
    );

    return this.statusChangeTargetActive
      ? `Al activar "${name}" volverá a estar disponible para nuevas reservas en el flujo de "Reserva tu hora". Los profesionales que la tengan asignada podrán continuar utilizándola.`
      : `Al desactivar "${name}" dejará de estar disponible para nuevas reservas en el flujo de "Reserva tu hora". Los profesionales seguirán teniéndola asignada.`;
  }

  public getStatusChangeConfirmText(): string {
    return this.statusChangeTargetActive
      ? 'Activar'
      : 'Desactivar';
  }

  public closeStatusChangeModal(): void {
    if (this.savingStatusChange) return;

    this.showStatusChangeModal = false;
    this.statusChangeSpecialty = null;
    this.statusChangeTargetActive = false;
  }

  private buildStatusChangePayload():
    UpdateSpecialtyPayload | null {

    if (!this.statusChangeSpecialty) return null;

    const specialty = this.statusChangeSpecialty;

    return {
      spe_id: Number(specialty?.spe_id),
      price: this.getSpecialtyPrice(specialty),
      spe_name: this.sanitizeSpecialtyDisplayName(
        specialty?.spe_name
      ),
      spe_sta_id: this.statusChangeTargetActive
        ? this.ACTIVE_STATUS_ID
        : this.INACTIVE_STATUS_ID,
      spe_min_age: Number(specialty?.spe_min_age ?? 0),
      spe_max_age: Number(specialty?.spe_max_age ?? 150),
      spe_is_ondemand: Boolean(
        specialty?.spe_is_ondemand
      ),
      spe_color_id: this.getSpecialtyColorId(specialty),
      spe_time_minutes: this.getSpecialtyDuration(specialty),
      cus_ids: this.extractSpecialtyCustomerIds(specialty)
    };
  }

  public async confirmStatusChange(): Promise<void> {
    if (
      !this.statusChangeSpecialty ||
      this.savingStatusChange
    ) {
      return;
    }

    const payload = this.buildStatusChangePayload();

    if (!payload) return;

    const specialtyName = payload.spe_name;
    const targetIsActive = this.statusChangeTargetActive;

    console.log(
      '[AdminEspecialidades] Payload cambio estado:',
      payload
    );

    try {
      this.savingStatusChange = true;

      const response: any =
        await this.tarifasService.postUpdateSpeciality(payload);

      if (response === undefined || response === null) {
        throw new Error(
          'postUpdateSpeciality no retornó respuesta.'
        );
      }

      this.showStatusChangeModal = false;
      this.statusChangeSpecialty = null;
      this.statusChangeTargetActive = false;

      await this.getCompleteSubSpecialityList();

      this.generalService.modalExitoso(
        targetIsActive
          ? 'Especialidad activada'
          : 'Especialidad desactivada',
        targetIsActive
          ? `"${specialtyName}" fue activada correctamente.`
          : `"${specialtyName}" fue desactivada correctamente.`
      );

    } catch (error) {
      console.error(
        '[AdminEspecialidades] Error cambiando estado:',
        error
      );

      this.generalService.modalError(
        'No fue posible cambiar el estado',
        `Ocurrió un problema al ${
          targetIsActive ? 'activar' : 'desactivar'
        } la especialidad.`
      );

    } finally {
      this.savingStatusChange = false;
    }
  }

  // =========================================================
  // NUMBER NULLABLE
  // =========================================================

  private normalizeNullableNumber(value: any): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numberValue = Number(value);

    return Number.isFinite(numberValue)
      ? numberValue
      : null;
  }
}